import {until} from 'lit-html/directives/until';
import {rosterErrors, modals} from '@modules/constants-enumerate';
import {rosterErrorLabels, tierStructure} from '@modules/i18n-labels';

const ROW_ERROR_TYPES = [
  rosterErrors.DUPLICATE_EMAIL,
  rosterErrors.DUPLICATE_PHONE,
  rosterErrors.EMAIL_IN_USE_BY_ADMIN,
  rosterErrors.ID_MISMATCH,
  rosterErrors.ID_REUSE_MISMATCH,
  rosterErrors.INVALID_EMAIL,
  rosterErrors.INVALID_NAME,
  rosterErrors.INVALID_COUNTRY_CODE,
  rosterErrors.INVALID_EXTERNAL_ID_LENGTH,
  rosterErrors.INVALID_LANGUAGE,
  rosterErrors.INVALID_MONTH,
  rosterErrors.INVALID_PHONE,
  rosterErrors.INVALID_SWAP_OF_GROUP_LEVEL,
  rosterErrors.INVALID_YEAR,
  rosterErrors.NO_EMAIL_OR_PHONE,
  rosterErrors.REPLACEMENT_ID_MISMATCH,
  rosterErrors.REPLACEMENT_ID_USED,
  rosterErrors.REPLACEMENT_ID_ARCHIVED,
  rosterErrors.REQUIRED,
  rosterErrors.INVALID_NAME
];

const dynamicClasses = [
  'badge-primary',
  'badge-secondary',
  'badge-dark',
  'badge-info',
];

// TODO: do we need roster type in here?

export default (ctx, html) => html`
  <p class="text-danger text-center">
    ${until(ctx.__('<tt-BqfMpLbH>', 'Please correct the following rows in your CSV and reupload your updated CSV'))}
    <show-modal id="info-btn" class="btn rounded-pill" modal-id="${modals.CSV_ROW_ERROR}">
      ${until(ctx.__('<tt-B3VO2h3t>', '?'))}
    </show-modal>
  </p>
  <div class="row">
    ${ctx.errors.map((error) => renderError(ctx, html, error))}
  </div>
`;

const getProvideUniqueRosterId = (ctx) => {
  return ctx.__('<tt-rwzkP7pY>', 'Your entry for user %r1% with %r2% matched multiple records. Please provide the Unique Roster ID of the proper match. You may also try a new name and contact combination or insert a new Unique Roster ID to create a new record.');
};

const HTML_ESCAPES = {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;'};

/**
 * `ctx._r` parses its result as an HTML template, so any value that originates
 * from the uploaded CSV must be escaped before it is substituted in.
 *
 * @param {*} value
 * @return {String}
 */
const escapeHtml = (value) => {
  const text = String(value === null || typeof value === 'undefined' ? '' : value);

  return text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
};

/**
 * Wraps a CSV-derived value in an escaped badge. An empty value renders a
 * placeholder so the message never contains an empty pill.
 *
 * @param {*} value
 * @return {String}
 */
const infoBadge = (value) => `<span class="badge badge-info">${escapeHtml(value) || '(unknown)'}</span>`;

/**
 * Maps the `object_type` sent by RosterJob (V1 and V2 DiffData) to the kind of
 * record the ambiguity was found for.
 */
const AMBIGUOUS_KINDS = {
  groups: 'group',
  classes: 'group',
  teachers: 'teacher',
  users: 'teacher',
  admins: 'admin',
  students: 'student',
};

/**
 * Looks up the kind for an `object_type`, ignoring inherited object keys such as `constructor`.
 *
 * @param {*} objectType
 * @return {String|null}
 */
const getAmbiguousKind = (objectType) => {
  return Object.prototype.hasOwnProperty.call(AMBIGUOUS_KINDS, objectType) ? AMBIGUOUS_KINDS[objectType] : null;
};

/**
 * Returns the first value that is not null, undefined or an empty string.
 *
 * @param {...*} values
 * @return {*}
 */
const firstPresent = (...values) => values.find((value) => value !== null && typeof value !== 'undefined' && value !== '');

/**
 * `payload.values` holds the lookup keys that matched more than one record. It is
 * an object for compound lookups (e.g. `{contact_email}`, `{name, path}`) but a
 * plain string for single-key lookups (e.g. a SIS id).
 *
 * @param {*} values
 * @return {Object}
 */
const normalizeMatchedValues = (values) => {
  if (values !== null && typeof values === 'object') {
    return values;
  }

  return {value: values};
};

/**
 * Students carry a `name`; teachers and admins only carry `first_name` and `last_name`.
 *
 * @param {Object} object
 * @return {String}
 */
const getObjectName = (object = {}) => {
  return firstPresent(object.name, [object.first_name, object.last_name].filter(Boolean).join(' ')) || '';
};

/**
 * Picks the most useful identifier to show for an ambiguous student, teacher or admin.
 *
 * @param {Object} values the matched lookup keys
 * @param {Object} object the row that was being imported
 * @return {String}
 */
const getPersonIdentifier = (values, object = {}) => {
  return firstPresent(
    values.contact_email,
    values.email,
    values.contact_phone,
    values.phone,
    values.external_id,
    values.sis_id,
    values.value,
    object.email,
    object.phone,
    object.external_id,
  ) || 'no email, phone, or ID';
};

/**
 * Renders the card for an `ambiguous` error. Never throws and never renders an
 * empty card, whatever shape the payload has.
 *
 * @param {Object} ctx
 * @param {Function} html
 * @param {Object} error
 * @return {Object}
 */
const renderAmbiguous = (ctx, html, error) => {
  const payload = error.payload || {};
  const object = payload.object || {};
  const values = normalizeMatchedValues(payload.values);
  const kind = getAmbiguousKind(payload.object_type);
  let label = '';
  let msg = '';

  if (kind === 'group') {
    if (payload.object_type === 'classes' || payload.level >= ctx.$classLevel) {
      label = tierStructure.CLASSES;
    } else {
      label = ctx.$myGroups[payload.level]?.label || 'Group';
    }

    let infoMessage = 'Your entry ';
    let matchedObject = '';
    if (values.name) {
      infoMessage += 'with name ';
      matchedObject = infoBadge(values.name);
    } else if (values.external_id) {
      infoMessage += 'with external id ';
      matchedObject = infoBadge(values.external_id);
    } else {
      matchedObject = infoBadge(firstPresent(values.sis_id, values.value, getObjectName(object)));
    }

    infoMessage += '%r1% matched multiple records. Please provide the Unique Roster ID of the proper match. You may also try a new name or insert a new Unique Roster ID to create a new record.';
    msg = ctx._r(ctx.__('<tt-oSHjt9CL>', infoMessage), matchedObject);
  } else if (kind === 'student' || kind === 'teacher' || kind === 'admin') {
    if (kind === 'student') {
      label = ctx.__('<tt-30yo5l2+>', 'Student');
    } else if (kind === 'teacher') {
      label = ctx.__('<tt-5nFMLfmz>', 'Teacher');
    } else {
      label = 'Admin';
    }

    msg = ctx._r(
      getProvideUniqueRosterId(ctx),
      infoBadge(getObjectName(object)),
      infoBadge(getPersonIdentifier(values, object)),
    );
  } else {
    // Never render an empty card for an object type this UI does not know about.
    label = typeof payload.object_type === 'string' && payload.object_type ? payload.object_type : 'Record';
    msg = ctx._r(
      'Your entry %r1% matched multiple records. Please provide the Unique Roster ID of the proper match.',
      infoBadge(firstPresent(
        getObjectName(object),
        values.contact_email,
        values.email,
        values.name,
        values.external_id,
        values.value,
      )),
    );
  }

  return html`
    <div class="col col-sm-6 col-md-4 mb-3">
      <div class="card card-border-color-left card-border-color-danger">
        <div class="card-header">
          ${until(rosterErrorLabels[error.type])}
          <div class="font-weight-bold">${until(label)}</div>
        </div>
        <p class="card-body">${html`${until(msg)}`}</p>
      </div>
    </div>
  `;
};

const buildBadge = (type, label) => ({type, label});

const buildBadges = (ctx, error, other) => {
  const badges = [];

  if (error.payload.file) {
    badges.push(buildBadge('primary', error.payload.file));
  }
  if (error.payload.line) {
    badges.push(buildBadge('dark', line(ctx, error.payload.line)));
  }

  if (other) {
    badges.push(buildBadge('info', error.payload[other]));
  }

  return badges;
};

const line = (ctx, line) => ctx._r(ctx.__('<tt-PSJSKrFb>', 'Line %r1%'), line);

const renderCard = (html, header, body, badges) => {
  let footer = null;
  if (badges) {
    footer = html`
      <div class="card-footer">
        ${badges?.map((badge) => html`
          <div class="badge badge-${badge.type} badge-pill">${until(badge.label)}</div>`)}
      </div>`;
  }

  return html`
    <div class="col col-sm-6 col-md-4 mb-3 d-flex align-items-stretch">
      <div class="card card-border-color-left card-border-color-danger w-100">
        <div class="card-header">${until(header)}</div>
        <div class="card-body">${until(body)}</div>
        ${footer}
      </div>
    </div>
  `;
};

const renderError = (ctx, html, error) => {
  if (ROW_ERROR_TYPES.indexOf(error.type) > -1) {
    return html`
      <div class="col col-sm-6 col-md-4 mb-3">
        <div class="card card-border-color-left card-border-color-danger">
          <div class="card-header">
            ${until(rosterErrorLabels[error.type])}
            <div class="font-weight-bold">${until(ctx.__('<tt-hu4qx1yd>', 'Row'))} ${error.payload.line}
              ${error.payload.column ? ', Column ' + ctx.numberToExcelCol(error.payload.column) : ''}
            </div>
          </div>
          <p class="card-body">
            ${until(ctx.__('<tt-91mNFPws>', 'If you want to use our template,'))} <a class="cursor-pointer text-blue"
                                                                                     @click="${ctx.getRosterTemplate}">${until(ctx.__('<tt-COWdCZBk>', 'download it here'))}</a>.
          </p>
        </div>
      </div>
    `;
  }

  if (error.type === rosterErrors.MISSING_HEADER) {
    return renderCard(html, ctx.__('<tt-OJP0HeWO>', 'Missing header'), ctx.__('<tt-rVdNvgSl>', 'A required header was not found'), buildBadges(ctx, error, 'header'));
  }

  if (error.type === rosterErrors.NO_SUCH_FIELD_ERROR) {
    return renderCard(html, ctx.__('<tt-yVDLz3Rd>', 'Required field missing'), ctx.__('<tt-lBw5z4/6>', 'A required field was not found'), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.NOT_EQUAL_ERROR) {
    return renderCard(html, ctx.__('<tt-lRezSx5U>', 'Not equal'), ctx._r(ctx.__('<tt-HGPXGhTg>', 'Expected %r1%, got %r2%'), error.payload.compared_value, error.payload.value), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.NO_SUCH_CHOICE_ERROR) {
    return renderCard(html, ctx.__('<tt-Nb5t+ZSJ>', 'Unknown value error'), `Valid values: ${error.payload.choices}`, buildBadges(ctx, error, 'property'));
  }
  if (error.type === rosterErrors.INVALID_NAME) {
    return renderCard(html, ctx.__('<tt-rmZuVr+R>', 'Invalid Name'), `Provided name not valid`, buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.MISSING_FIELD_ERROR) {
    return renderCard(html, ctx.__('<tt-daDD+YhO>', 'Missing field error'), ctx.__('<tt-0Nd0BP5w>', 'Required field missing'), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.ID_MISMATCH_NEW) {
    const badges = buildBadges(ctx, error, 'property');
    badges.push(buildBadge('secondary', error.payload.user_type));
    return renderCard(html, ctx.__('<tt-2SJTTYDx>', 'Unique Roster ID and Email conflict'), ctx.__('<tt-bYGFbjzU>', 'Email is in use by another user'), badges);
  }

  if (error.type === rosterErrors.INVALID_DATE_FORMAT_ERROR) {
    return renderCard(html, ctx.__('<tt-aStCttGG>', 'Invalid date error'), ctx._r(ctx.__('<tt-bf6VDqNj>', 'Provided date is not valid. Sample date: <code>%r1%</code>'), error.payload.sample_value), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.INVALID_USER_ROLE_REFERENCE) {
    return renderCard(html, ctx.__('<tt-7fcAuPpy>', 'Invalid role'), ctx._r(ctx.__('<tt-CWyR4g0y>', '<code>%r1%</code> was not found with a corresponding role in <code>%r2%</code>'), error.payload.invalid_value, error.payload.reference), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.INCOMPLETE_DELETION) {
    let msg;
    if (error.payload.type === 'classroom') {
      msg = ctx._r(ctx.__('<tt-Btae4aNY>', 'Class with ID <code>%r1%</code> is marked for archival, however not all enrollments are removed.'), error.payload.external_id)
    } else if (error.payload.type === 'user') {
      msg = ctx._r(ctx.__('<tt-mEpJk5Y7>', 'User with ID <code>%r1%</code> is marked for archival, however not all roles/enrollments are removed.'), error.payload.external_id)
    } else if (error.payload.type === 'management') {
      msg = ctx._r(ctx.__('<tt-ICiBxZ0r>', 'Org with ID <code>%r1%</code> is marked for archival, however not all roles/enrollments are removed.'), error.payload.external_id);
    }

    return renderCard(html, ctx.__('<tt-J0qfkgll>', 'Consistency Error'), msg);
  }

  if (error.type === rosterErrors.INVALID_CLASS_PARENT) {
    let msg;
    if (ctx.tierLevels === 1) {
      msg = ctx._r(ctx.__('<tt-yxY9XpYT>', `<code>%r1%</code> must be your organization's ID`), error.payload.invalid_value);
    } else {
      msg = ctx._r(ctx.__('<tt-3EoC9yX+>', '<code>%r1%</code> must be a group of type <code>%r2%</code>'), error.payload.invalid_value, ctx.$myGroups[ctx.$classLevel - 1].label);
    }
    return renderCard(html, ctx.__('<tt-wsmVIOOB>', 'Invalid Class Parent Group'), msg, buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.INVALID_ORG_STATUS) {
    return renderCard(html, ctx.__('<tt-M/HnXEf+>', 'Invalid Status'), ctx._r(ctx.__('<tt-qg9Cg8c5>', 'This record must be marked as <code>tobedeleted</code> because parent org <code>%r1%</code> is not active'), error.payload.invalid_value), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.INVALID_ORG_STRUCTURE) {
    return renderCard(html, ctx.__('<tt-yjnPZvyC>', 'Invalid Organization Structure'), ctx.__('<tt-qM1z+IlI>', 'Your uploaded file contains organizations that do not conform to your Educator Center structure.'), buildBadges(ctx, error));
  }

  if (error.type === rosterErrors.IS_BLANK_ERROR) {
    return renderCard(html, ctx.__('<tt-I2BcDl4d>', 'Is blank error'), ctx.__('<tt-q/QfY3ZL>', 'Value for required field is blank'), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.INVALID_PHONE_FORMAT_ERROR) {
    return renderCard(html, ctx.__('<tt-B4ulEgyq>', 'Invalid phone format'), ctx.__('<tt-ZNF4++ow>', 'Invalid phone format'), buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.UNKNOWN_HEADER) {
    return renderCard(html, ctx.__('<tt-AxvyH03s>', 'Unknown header'), ctx.__('<tt-pCugyDxR>', 'This header must not be in CSV'), buildBadges(ctx, error, 'header'));
  }

  if (error.type === rosterErrors.FILE_NOT_FOUND_ERROR) {
    return renderCard(html, ctx.__('<tt-lslbf+K7>', 'File not found'), ctx.__('<tt-4BXn+xrV>', 'File was not found in the upload'), buildBadges(ctx, error));
  }

  if (error.type === rosterErrors.INVALID_REFERENCE) {
    const invalidValue = Array.isArray(error.payload.invalid_value) ? error.payload.invalid_value.join(', ') : error.payload.invalid_value;
    const msg = ctx._r(ctx.__('<tt-fA4Ncuk7>', 'A reference to <code>%r1%</code> was not found in <code>%r2%</code>'), invalidValue, error.payload.reference);
    return renderCard(html, rosterErrorLabels[error.type], msg, buildBadges(ctx, error, 'property'));
  }

  if (error.type === rosterErrors.INVALID_MANAGED_RECORD_STATUS) {
    return renderCard(html,
      ctx.__('<tt-ANz/S2j6>', 'Reference record is inactive'),
      ctx._r(ctx.__('<tt-2eZsY96F>', 'Reference record is inactive: %r1%'), error.payload.external_id),
      buildBadges(ctx, error));
  }

  if (error.type === rosterErrors.AMBIGUOUS) {
    return renderAmbiguous(ctx, html, error);
  }

  return '';
};
